import {
  MAX_LEVEL,
  SKILL_IDS,
  SKILL_MAX,
  levelFor,
  skillMods,
  xpToNext,
  type LifetimeStats,
  type ProgressState,
  type SkillId,
} from "@rietz/shared";
import type { LeaderboardRow } from "../store.js";
import { formatCount } from "./format.js";

/**
 * The profile tab's arithmetic, with no React in it — same split as
 * `hideoutLogic.ts`. Almost everything here is a SENTENCE OR A RATIO, which a
 * markup smoke is bad at pinning down. Nothing here is a decision: the tab is
 * a readout, and the level gate lives in `offerLogic.ts`. Every rate is a
 * whole percent (the numbers move once per raid); the kill/death ratio alone
 * is read to two places, as the genre does.
 */

// ---------------------------------------------------------------------------
// The ladder
// ---------------------------------------------------------------------------

/** The header's level badge and the bar under it, ready to draw. */
export interface XpView {
  level: number;
  /** True at `MAX_LEVEL`: there is no next rung and the bar is not a progress
   *  bar any more but a statement. */
  atMax: boolean;
  /** 0..1. Exactly 1 at the top of the ladder, which is the only honest way to
   *  draw a bar with nothing left in front of it. */
  fill01: number;
  /** `"4,266 / 5,172 XP"`, or `"MAX"` at the ceiling. */
  label: string;
  /** `"49,103 XP"` — the running total, which is the number a player quotes. */
  totalLabel: string;
}

/**
 * How far up the ladder `xp` stands. `xpToNext` does the arithmetic (`null` at
 * the top); the two numbers in `label` are measured FROM the current threshold,
 * or the bar would sit motionless for ten levels.
 */
export function xpView(xp: number): XpView {
  const level = levelFor(xp);
  const next = xpToNext(xp);
  const total = formatCount(xp);
  if (next === null) {
    return { level, atMax: true, fill01: 1, label: "MAX", totalLabel: `${total} XP` };
  }
  return {
    level,
    atMax: false,
    fill01: next.need > 0 ? clamp01(next.have / next.need) : 0,
    label: `${formatCount(next.have)} / ${formatCount(next.need)} XP`,
    totalLabel: `${total} XP`,
  };
}

/** The top of the ladder, re-exported so the tab can print "Level 7 of 30"
 *  without importing the rules it is only quoting. */
export const TOP_LEVEL = MAX_LEVEL;

// ---------------------------------------------------------------------------
// The four skills
// ---------------------------------------------------------------------------

/** One skill's bar, and the one thing it is worth. */
export interface SkillView {
  id: SkillId;
  /** "Endurance". Title case, because it is a proper noun on this screen. */
  label: string;
  /** The raw 0..100 value, for the bar. */
  value: number;
  fill01: number;
  /** `"73"` — whole, because the fractions exist to make a raid move the bar
   *  and not to be read. */
  valueLabel: string;
  /** `"+15 % stamina regen"`. The multiplier this much of the skill buys,
   *  signed in the direction it actually moves the simulation. */
  effect: string;
  /**
   * What raises it, in one verb — skills grow from USE, so the screen must say
   * which.
   */
  raisedBy: string;
}

/**
 * Each skill's display name and verb. English on a screen, so not in `shared/`.
 */
const SKILL_TEXT: Record<SkillId, { label: string; raisedBy: string }> = {
  endurance: { label: "Endurance", raisedBy: "Raised by sprinting" },
  strength: { label: "Strength", raisedBy: "Raised by carrying weight" },
  vitality: { label: "Vitality", raisedBy: "Raised by taking damage" },
  gunsmith: { label: "Gunsmith", raisedBy: "Raised by firing rounds" },
};

/**
 * The four bars in `SKILL_IDS` order. The effect sentence is computed from
 * `skillMods` — the shared function the SERVER simulates with — never from the
 * skill value, so the sentence and the body are two readings of one
 * calculation.
 */
export function skillViews(skills: Record<SkillId, number>): SkillView[] {
  const mods = skillMods(skills);
  const effects: Record<SkillId, string> = {
    endurance: signedPercent(mods.staminaRegenMult, "stamina regen"),
    strength: signedPercent(mods.carryMult, "carry weight"),
    vitality: signedPercent(mods.bleedChanceMult, "bleed chance"),
    gunsmith: signedPercent(mods.wearMult, "weapon wear"),
  };
  return SKILL_IDS.map((id) => {
    const value = clampSkill(skills[id]);
    return {
      id,
      label: SKILL_TEXT[id].label,
      value,
      fill01: clamp01(value / SKILL_MAX),
      valueLabel: String(Math.floor(value)),
      effect: effects[id],
      raisedBy: SKILL_TEXT[id].raisedBy,
    };
  });
}

/**
 * `1.12` + `"carry weight"` → `"+12 % carry weight"`; `0.91` + `"bleed chance"`
 * → `"−9 % bleed chance"`. Signed in the direction the multiplier moves, never
 * flipped into a "resistance" the sim does not have. A fresh skill prints `"+0
 * % …"`.
 */
function signedPercent(mult: number, noun: string): string {
  const pct = Number.isFinite(mult) ? Math.round((mult - 1) * 100) : 0;
  return `${pct < 0 ? "−" : "+"}${Math.abs(pct)} % ${noun}`;
}

// ---------------------------------------------------------------------------
// The tally
// ---------------------------------------------------------------------------

/** One cell of the lifetime grid: a label and the number under it. */
export interface StatView {
  label: string;
  value: string;
}

/**
 * Everything the account has done, as nine cells — seven counters and two
 * derived (survival rate beside the extractions, the ratio beside the kills),
 * not marked apart.
 */
export function lifetimeViews(stats: LifetimeStats): StatView[] {
  return [
    { label: "Raids", value: formatCount(count(stats.raids)) },
    { label: "Extracted", value: formatCount(count(stats.extracts)) },
    { label: "Survival rate", value: formatPercent(survivalRate(stats)) },
    { label: "Killed", value: formatCount(count(stats.deaths)) },
    { label: "Missing", value: formatCount(count(stats.mia)) },
    { label: "K / D", value: formatRatio(killDeathRatio(stats)) },
    { label: "Poachers killed", value: formatCount(count(stats.scavKills)) },
    { label: "Mercenaries killed", value: formatCount(count(stats.pmcKills)) },
    { label: "The Hunter killed", value: formatCount(count(stats.bossKills)) },
  ];
}

/**
 * Extractions over raids, or `null` for an account that has never deployed — "0
 * %" would be a claim it has not made. Printed as an em dash.
 */
export function survivalRate(stats: LifetimeStats): number | null {
  const raids = count(stats.raids);
  if (raids === 0) return null;
  return count(stats.extracts) / raids;
}

/**
 * Kills over deaths, with the genre's `max(1, deaths)`. Bosses are NOT in the
 * numerator: the Hunter has his own line, and folding one kill into hundreds
 * would lose it.
 */
export function killDeathRatio(stats: LifetimeStats): number {
  const kills = count(stats.scavKills) + count(stats.pmcKills);
  const deaths = count(stats.deaths);
  return kills / (deaths > 0 ? deaths : 1);
}

/** `0.615` → `"62 %"`, `null` → `"—"`. Whole percent — see the header. */
export function formatPercent(fraction: number | null): string {
  if (fraction === null || !Number.isFinite(fraction)) return "—";
  return `${Math.round(fraction * 100)} %`;
}

/** `1.75` → `"1.75"`. Two places, the way every other shooter prints one. */
export function formatRatio(n: number): string {
  return Number.isFinite(n) ? n.toFixed(2) : "0.00";
}

// ---------------------------------------------------------------------------
// The board
// ---------------------------------------------------------------------------

/** One line of the ladder, numbered, and flagged when it is ours. */
export interface BoardRowView {
  /** 1-based position, which is the SERVER'S order and not a sort of ours. */
  rank: number;
  row: LeaderboardRow;
  /** The one line drawn lit. See `boardViews` for why there is only ever one. */
  me: boolean;
}

/**
 * Numbers the board and finds the player. THE SERVER'S ORDER IS THE RANKING,
 * kept as it arrived. The highlight is the FIRST match only: a display name is
 * neither unique nor stable, the board carries no usernames, and lighting both
 * Sepps would be a claim about somebody else. A `null` name lights nothing.
 */
export function boardViews(
  rows: readonly LeaderboardRow[],
  myName: string | null,
): BoardRowView[] {
  let found = false;
  return rows.map((row, i) => {
    const me = !found && myName !== null && row.name === myName;
    if (me) found = true;
    return { rank: i + 1, row, me };
  });
}

// ---------------------------------------------------------------------------

/**
 * One whole progression in the three shapes the tab draws — one `useMemo`. Does
 * NOT sanitize: `sanitizeProgress` ran at the wire boundary. The clamps at the
 * foot of the file are narrower: local arithmetic that must not print `NaN %`.
 */
export function progressViews(progress: ProgressState): {
  xp: XpView;
  skills: SkillView[];
  stats: StatView[];
} {
  return {
    xp: xpView(progress.xp),
    skills: skillViews(progress.skills),
    stats: lifetimeViews(progress.stats),
  };
}

function clamp01(n: number): number {
  if (!Number.isFinite(n) || n <= 0) return 0;
  return n > 1 ? 1 : n;
}

function clampSkill(n: number | undefined): number {
  if (typeof n !== "number" || !Number.isFinite(n) || n <= 0) return 0;
  return n > SKILL_MAX ? SKILL_MAX : n;
}

function count(n: number): number {
  return typeof n === "number" && Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
}
